import { useEffect, useState } from 'react';
import DrawerFlow, { type DrawerFlowStep } from 'src/UI/drawer-flow/DrawerFlow';
import GenerateStep from 'src/secret-menu/brand-asset-builder/steps/GenerateStep';
import ReviewPaletteStep from 'src/secret-menu/brand-asset-builder/steps/ReviewPaletteStep';
import SelectTenantStep from 'src/secret-menu/brand-asset-builder/steps/SelectTenantStep';
import TitlePageStep from 'src/secret-menu/brand-asset-builder/steps/TitlePageStep';
import UploadLogoStep from 'src/secret-menu/brand-asset-builder/steps/UploadLogoStep';
import {
  initialBrandAssetBuilderData,
  type BrandAssetBuilderData,
} from 'src/secret-menu/brand-asset-builder/types';

type BrandAssetBuilderDrawerProps = {
  opened: boolean;
  close: () => void;
};

const steps: DrawerFlowStep<BrandAssetBuilderData>[] = [
  {
    title: 'Select tenant',
    screens: [
      { component: SelectTenantStep, allowVerticalOverflow: true, maxContainerWidth: 'lg' },
    ],
  },
  {
    title: 'Upload logo',
    screens: [{ component: UploadLogoStep, allowVerticalOverflow: true, maxContainerWidth: 'lg' }],
  },
  {
    // No allowVerticalOverflow here (unlike the other steps): this step's own two-column layout
    // manages its own scrolling internally (see ReviewPaletteStep.tsx) so the preview column can
    // stay fixed in place while only the controls column scrolls — that only works if this
    // screen has a real, bounded height to divide between the two columns, rather than growing
    // to fit content and deferring to the drawer's own outer scroll area.
    title: 'Review palette',
    screens: [{ component: ReviewPaletteStep, maxContainerWidth: 'lg' }],
  },
  {
    // Same reasoning as the "Review palette" screen above — see TitlePageStep.tsx.
    title: 'Title page',
    screens: [{ component: TitlePageStep, maxContainerWidth: 'lg' }],
  },
  {
    title: 'Generate',
    screens: [{ component: GenerateStep, allowVerticalOverflow: true, maxContainerWidth: 'lg' }],
  },
];

export default function BrandAssetBuilderDrawer({ opened, close }: BrandAssetBuilderDrawerProps) {
  const [data, setData] = useState<BrandAssetBuilderData>(initialBrandAssetBuilderData);

  useEffect(() => {
    if (opened) {
      setData(initialBrandAssetBuilderData);
    }
  }, [opened]);

  const handleClose = () => {
    if (data.logoUrl) {
      URL.revokeObjectURL(data.logoUrl);
    }
    close();
  };

  return (
    <DrawerFlow
      opened={opened}
      close={handleClose}
      title="Brand asset builder"
      steps={steps}
      initialData={initialBrandAssetBuilderData}
      data={data}
      setData={setData}
      drawerProps={{ closeOnEscape: false }}
    />
  );
}
